import type { Cell, ConfigSummary } from "../api/types.gen";

/**
 * How a configuration is labelled and coloured on the dashboard.
 *
 * This module exists because the design prototype's equivalents were all
 * `row.id.startsWith("codex")` and `row.id === "plan-build"` — switches on the
 * ids of fourteen hardcoded fixture rows. That works exactly until real data
 * arrives, at which point every agent that is not Codex is rendered as Claude
 * Code, in Claude Code's colour, with Claude Code's name.
 *
 * Everything here is driven by `tool` and `tool_kind`, which are real fields on
 * every cell. The one judgement call is the tool→brand mapping, which is a
 * lookup with an explicit fallback rather than a chain of `startsWith`: an
 * unrecognized tool gets a neutral colour and its own name, which is a correct
 * rendering of a tool we have not met, not a wrong rendering of one we have.
 */

/** A known tool's presentation. Unknown tools fall through to `neutral`. */
interface Brand {
  /** The human name for the agent, when the raw tool id is not one. */
  label: string;
  /** The bar colour for a comparable row. */
  color: string;
  /** The vendor glyph drawn under the axis. One character, not a logo path. */
  glyph: string;
}

/**
 * The tools we can name, keyed by the CLI's own `tool` value.
 *
 * Adding one is a one-line change here and nothing else. This is a display
 * table, so a tool missing from it is a cosmetic gap and never a data gap — the
 * cell is still grouped, ranked and charted under its own id.
 */
const BRANDS: Record<string, Brand> = {
  "claude-code": { label: "Claude Code", color: "#c96443", glyph: "A" },
  codex: { label: "Codex", color: "#151515", glyph: "O" },
  cursor: { label: "Cursor", color: "#2849df", glyph: "C" },
  aider: { label: "Aider", color: "#168f83", glyph: "A" },
  gemini: { label: "Gemini CLI", color: "#1681e8", glyph: "G" },
  opencode: { label: "opencode", color: "#6f65d2", glyph: "O" },
};

/**
 * The colour a row that cannot be compared is drawn in.
 *
 * Grey is doing real work here: a `limited` or `unavailable` row sits in the
 * same chart as the comparison, and colouring it like a competitor invites the
 * reader to compare it with them. Draining the colour is the visual form of the
 * label beside it.
 */
const MUTED = "#b2abbc";

/** An unrecognized tool: named by its own id, drawn in a colour it shares with
 *  no brand, so it is visibly a tool rather than visibly the wrong tool. */
const NEUTRAL: Brand = { label: "", color: "#5b6470", glyph: "?" };

function brandFor(tool: string): Brand {
  return BRANDS[tool] ?? { ...NEUTRAL, label: tool };
}

/** The agent's name — "Claude Code", or the raw tool id for one we cannot name. */
export function agentName(row: ConfigSummary): string {
  return brandFor(row.tool).label || row.tool;
}

/**
 * The model line under the agent name.
 *
 * A workflow has no model of its own — it is a pipeline of steps that each have
 * one — so it says so rather than printing an empty line. `tool_kind` is what
 * distinguishes the two, which is why it is published.
 */
export function modelName(row: ConfigSummary): string {
  if (row.model === "") return row.effort === "" ? "workflow" : row.effort;
  return row.effort === "" ? row.model : `${row.model} · ${row.effort}`;
}

/**
 * The third axis line: which swept options this configuration set.
 *
 * The prototype hardcoded six phrasings ("context mode", "caveman", "rtk
 * enabled"…). These come from `axis_options`, so a new axis needs no code. The
 * `name=value` pairs are shortened only by dropping a redundant `=on`, which is
 * what a boolean axis reads as in the CLI.
 */
export function variantLabel(row: ConfigSummary): string {
  if (row.options.length === 0) return "default settings";
  return row.options
    .map((pair) => (pair.endsWith("=on") ? pair.slice(0, -3) : pair))
    .join(" · ");
}

/** The three-line axis label Chart.js renders under each bar. */
export function axisLabel(row: ConfigSummary): string[] {
  return [agentName(row), modelName(row), variantLabel(row)];
}

/** The bar colour: the tool's, unless the row is not part of the comparison. */
export function rowColor(row: ConfigSummary): string {
  return row.coverage_state === "comparable" ? brandFor(row.tool).color : MUTED;
}

/** The glyph drawn under the axis, identifying the vendor at a glance. */
export function rowGlyph(row: ConfigSummary): string {
  return brandFor(row.tool).glyph;
}

/** What a coverage state means, in the words the chart footer uses. */
export function stateLabel(row: ConfigSummary): string {
  switch (row.coverage_state) {
    case "comparable":
      return "Comparable";
    case "limited":
      return "Limited coverage · unranked";
    default:
      return "Unavailable · unranked";
  }
}

/**
 * Whether one cell belongs to one configuration.
 *
 * SELECTION, not aggregation — which is what keeps it on the right side of the
 * rule that there is exactly one leaderboard. Nothing here derives a rate, a
 * rank or a coverage state; it answers "which cell should the trajectory
 * teaser open on", and the server has already decided every number that will
 * be shown about it.
 *
 * It is nonetheless a partial restatement of `results.ConfigKey`, so it is
 * worth being precise about the blast radius if the two ever disagree: the
 * teaser opens on a cell from a neighbouring configuration. It cannot produce a
 * wrong number, because every number on that screen comes from the cell it
 * opened. That is why this compares the four published fields rather than
 * rebuilding the key string — a key that is ALMOST right silently matches
 * nothing, while four fields that are almost right match something close.
 *
 * The cell's options are resolved through `axis_options` exactly as
 * `results.SortedOptions` does: which axes were SWEPT decides the identity, and
 * an option set outside the sweep is a constant of the whole experiment rather
 * than a dimension of it.
 */
export function cellMatchesConfig(cell: Cell, config: ConfigSummary): boolean {
  if (cell.tool !== config.tool) return false;
  if (cell.model !== config.model) return false;
  if (cell.effort !== config.effort) return false;

  const options = sortedOptions(cell);
  if (options.length !== config.options.length) return false;
  return options.every((pair, index) => pair === config.options[index]);
}

/** The swept axes as sorted `name=value` pairs. `results.SortedOptions`. */
function sortedOptions(cell: Cell): string[] {
  return [...cell.axis_options]
    .sort()
    .map((name) => `${name}=${cell.options[name] ?? ""}`);
}
